import React, { useCallback, useEffect, useRef, useState } from "react";
import { readAuthorSetupIdentity } from '../../auth/authorSetup';
import { hasExistingMembership, membershipMessage, validateMembershipStatus } from '../../utils/membershipState';
import groupConfig from '../../services/publishingGroupConfig.json';
import { ExternalLink } from "lucide-react";
import { Link } from 'react-router-dom';

import ReaderAppreciationSettings from "./ReaderAppreciationSettings";

function displayDate(value) {
  if (!Number.isFinite(value) || value <= 0) return null;
  const date = new Date(value * 1000);
  return Number.isFinite(date.getTime()) ? date.toLocaleDateString(undefined, { dateStyle: 'long' }) : null;
}

export default function MembershipPage() {
  const [membership, setMembership] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [opening, setOpening] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [autoBillingConsent, setAutoBillingConsent] = useState(false);
  const [loading, setLoading] = useState(true);
  const owner = useRef(null);
  const openingLock = useRef(false);
  const requestNumber = useRef(0);
  const mounted = useRef(false);
  const load = useCallback(async () => {
    if (openingLock.current) return;
    const request = ++requestNumber.current;
    setLoading(true);
    setAutoBillingConsent(false);
    try {
      const identity = await readAuthorSetupIdentity();
      const response = await fetch('https://7n4zqfucx3.execute-api.us-west-2.amazonaws.com/default/checkMembershipStatus', {
        method: 'POST', headers: { Authorization: `Bearer ${identity.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ userSub: identity.userSub }), signal: AbortSignal.timeout(20000),
      });
      if (!response.ok) throw new Error('Status unavailable');
      const data = validateMembershipStatus(await response.json());
      if (mounted.current && request === requestNumber.current) {
        owner.current = identity.userSub;
        setMembership(data); setStatusError('');
      }
    } catch {
      if (mounted.current && request === requestNumber.current) {
        owner.current = null; setMembership(null);
        setStatusError('Your membership status is temporarily unavailable. Please refresh to review your current terms. You can still open Stripe to manage your membership.');
      }
    } finally { if (mounted.current && request === requestNumber.current) setLoading(false); }
  }, []);
  const trialNeedsConsent = membership?.billing?.status === 'trialing' && membership.billing.trialBillingPolicy === 'trial-opt-in-v1' && membership.billing.autoBillingConsent !== true;
  const billing = membership?.billing;
  // BC memberships are priced in USD. Do not invent terms for an unknown rate.
  const planRate = Number.isSafeInteger(billing?.planUnitAmount) && billing.planUnitAmount >= 0 && billing.planCurrency === 'usd' && ['month', 'year'].includes(billing.planInterval)
    ? `${new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(billing.planUnitAmount / 100)}/${billing.planInterval}` : null;
  const trialEndDate = displayDate(billing?.trialEnd);
  const canConsent = Boolean(planRate && trialEndDate && billing?.subscriptionId);
  const openBilling = async (manageOnly = false) => {
    if (openingLock.current || loading || (trialNeedsConsent && !manageOnly && (!autoBillingConsent || !canConsent))) return;
    if (!window.dispatchEvent(new Event('bookish:before-navigation', { cancelable: true }))) return;
    openingLock.current = true;
    setOpening(true);
    setBillingError('');
    try {
      const identity = await readAuthorSetupIdentity();
      if (owner.current && identity.userSub !== owner.current) throw new Error('Your Author account changed. Refresh membership status before continuing.');
      const consent = trialNeedsConsent && !manageOnly && autoBillingConsent;
      const response = await fetch(`${groupConfig.apiBase}/author-billing`, {
        method: 'POST', headers: { Authorization: `Bearer ${identity.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ autoBillingConsent: consent, ...(manageOnly ? { manageOnly: true } : {}), ...(consent ? { reviewedPlan: {
          subscriptionId: billing.subscriptionId, trialEnd: billing.trialEnd, planUnitAmount: billing.planUnitAmount,
          planCurrency: billing.planCurrency, planInterval: billing.planInterval,
        } } : {}) }), signal: AbortSignal.timeout(20000),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || 'Unable to open billing. Please try again.');
      const url = new URL(data.url);
      if (url.protocol !== 'https:' || url.hostname !== 'billing.stripe.com' || url.username || url.password || url.port) throw new Error('Unable to open the secure billing portal.');
      if (!mounted.current) return;
      const currentIdentity = await readAuthorSetupIdentity();
      if (currentIdentity.userSub !== identity.userSub) throw new Error('Your Author account changed. Refresh membership status before continuing.');
      window.location.assign(url.href);
    } catch (error) {
      if (mounted.current) {
        setAutoBillingConsent(false);
        setBillingError(`${error.message || 'Unable to open billing.'} Refresh membership status before retrying; your last request may have been received.`);
      }
    } finally { openingLock.current = false; if (mounted.current) setOpening(false); }
  };
  useEffect(() => {
    mounted.current = true;
    load();
    window.addEventListener('focus', load);
    return () => { mounted.current = false; window.removeEventListener('focus', load); };
  }, [load]);
  const endDate = displayDate(billing?.currentPeriodEnd);

  return (
    <section aria-labelledby="membership-heading" className="mx-auto max-w-3xl space-y-6 text-bookish-purple">
      <div className="rounded-xl border border-bookish-purple/20 bg-bookish-cream/30 p-5">
        <h2 id="membership-heading" className="text-2xl font-serif font-bold mb-3">Membership &amp; Billing</h2>
        <div role="status" className="mb-4 text-sm">
          {loading ? 'Loading membership status…' : statusError || (!membership ? 'Membership status unavailable.' : billing?.cancelAtPeriodEnd
            ? `Cancellation scheduled${endDate ? ` — membership ends ${endDate}` : ''}.`
            : membershipMessage(membership))}
        </div>
        <button type="button" onClick={load} disabled={loading || opening} className="mb-4 min-h-[44px] underline disabled:opacity-50 focus-visible:outline focus-visible:outline-2">{loading ? 'Refreshing…' : 'Refresh membership status'}</button>
        <p className="text-bookish-purple/80 mb-6 leading-relaxed text-sm">
          Manage your Bookish Circle membership and payments securely through Stripe.
        </p>
        {membership?.billing?.status === 'trialing' && membership.billing.trialBillingPolicy === 'trial-opt-in-v1' && <div className="mb-5 rounded-lg border border-bookish-purple/20 p-4 text-sm">
          <p>Your trial{trialEndDate ? ` ends ${trialEndDate}` : ' is active'}. {trialNeedsConsent ? 'Automatic billing is off. To continue beyond a trial, you must agree below and save a card in Stripe.' : 'You have authorized automatic billing. Stripe needs a saved payment method for any renewal after your trial; without one, your membership becomes inactive when the trial ends.'} {billing.cancelAtPeriodEnd && 'Cancellation is scheduled. Adding a card alone does not undo that cancellation.'}</p>
          {trialNeedsConsent && canConsent && <label className="flex items-start gap-3 mt-3 py-2 min-h-[44px]"><input type="checkbox" className="mt-1" disabled={loading || opening} checked={autoBillingConsent} onChange={event => setAutoBillingConsent(event.target.checked)} /><span>I agree to automatically charge my card at {planRate} when my trial ends and renew until I cancel. Review any discounts and taxes in Stripe before saving your card.</span></label>}
          {trialNeedsConsent && !canConsent && <p className="mt-3">Your plan details are unavailable. Refresh membership status before activating billing.</p>}
        </div>}
        <ul className="mb-6 list-disc space-y-2 pl-5 text-sm">
          <li>Update or replace your membership payment method.</li>
          <li>Review outstanding invoices and resolve a declined payment.</li>
          <li>Review available monthly and annual plans before confirming a change.</li>
          <li>Download invoices or cancel your subscription.</li>
        </ul>

        <button
          type="button"
          onClick={() => openBilling()}
          disabled={loading || opening || (trialNeedsConsent && (!autoBillingConsent || !canConsent))}
          className="inline-flex min-h-[44px] items-center gap-2 bg-bookish-purple text-white px-5 py-3 rounded-md font-semibold hover:bg-bookish-purpleAccent disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
        >
          <ExternalLink size={16} aria-hidden="true" />
          {opening ? 'Opening secure billing…' : trialNeedsConsent ? 'Activate billing — add my card' : 'Manage membership & payments'}
        </button>
        {trialNeedsConsent && <div className="mt-3"><button type="button" onClick={() => openBilling(true)} disabled={loading || opening} className="min-h-[44px] underline disabled:opacity-50 focus-visible:outline focus-visible:outline-2">Review or cancel my trial</button><p className="text-xs">No agreement to automatic billing is needed to review or cancel your trial.</p></div>}
        {billingError && <p role="alert" className="mt-3 text-sm text-red-700">{billingError}</p>}

        <p className="mt-4 text-xs text-bookish-black/70">
          Stripe shows any charges and the effective date before you confirm a change.
        </p>
      </div>
      <ReaderAppreciationSettings />
      <div className="rounded-xl border border-bookish-purple/20 p-5">
        <h3 className="text-lg font-semibold">Payment declined?</h3>
        <p className="mt-2 text-sm leading-relaxed">Open the billing portal to check your invoice and update your payment method. If an invoice is still unpaid, follow its payment instructions to resolve the balance.</p>
      </div>
      <div className="rounded-xl border border-bookish-purple/20 p-5">
        <h3 className="text-lg font-semibold">Thinking of leaving the Circle?</h3>
        <p className="mt-2 text-sm leading-relaxed">We’d be sorry to see you go. You can cancel through the billing portal. Your membership continues until the end of your current paid period or trial; review the end date in Stripe before confirming.</p>
        <ul className="mt-3 list-disc space-y-2 pl-5 text-sm">
          <li>When membership ends, your shop, books, and blogs are hidden from the public. Publishing books or blogs and sending newsletters are disabled.</li>
          <li>Your pen name and subdomain stay reserved for one year (365 days) after membership ends, then become available for other members to claim. This reservation does not extend membership access.</li>
          <li>Your BC email address stays permanently reserved to you and will never be reassigned to another author.</li>
          <li>The Bookish Circle keeps your saved data. Deactivation does not delete your account, books, or order history.</li>
        </ul>
        <p className="mt-3 text-sm">You’re welcome to return to the Circle. Reactivate within the reservation period to keep your pen name and subdomain. After that period, your previous names may have been claimed by someone else.</p>
        {!loading && !statusError && membership?.returningMember && !hasExistingMembership(membership) && !billing?.cancelAtPeriodEnd && !billing?.paymentRequired && (
          <Link className="mt-4 inline-block underline font-semibold" to="/authorportal/mainpage?tab=getstarted">Return to the Circle</Link>
        )}
      </div>
      <p className="text-sm">Belong to a Publishing Group? <Link className="underline" to="/publishing-groups/author-membership">Manage your group connection and membership pricing</Link>.</p>
      <p className="text-xs">For payments from your book sales, use My Stripe Dashboard in Settings.</p>
    </section>
  );
}
